/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { GTGT_FILTER_NAME, getGtgtDisplayValue } from '@plp/data-access/constants';

import { Chain } from '@product-recommendations/data-access';
import { FacetVOValue } from '@plp/data-access/interfaces';
import { FilterOptionComponent } from '../filter-option/filter-option.component';
import { FilterOptionType } from './../filter-option/filter-option.enum';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';

@Component({
  selector: 'plp-components-standard-filter',
  templateUrl: './standard-filter.component.html',
  styleUrl: './standard-filter.component.scss',
  imports: [FilterOptionComponent, HmfActionTextComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardFilterComponent {
  private readonly chain = inject(Chain);

  readonly filterOptions = input.required<FacetVOValue[]>();
  readonly attributeName = input.required<string>();
  readonly attributeIdentifier = input.required<string>();
  readonly defaultShowMore = input<boolean>(false);
  readonly allowCollapsing = input<boolean>(true);
  readonly selectedOptions = input<string[]>([]);
  readonly isMobile = input<boolean>(false);
  readonly disabled = input<boolean>(false);

  readonly selectedFilter = output<string>();
  readonly showMoreChange = output<boolean>();

  GTGT_FILTER_NAME = GTGT_FILTER_NAME;
  FilterOptionType = FilterOptionType;

  showMore = linkedSignal<boolean>(() => {
    return !this.allowCollapsing() || this.defaultShowMore();
  });

  // Replace setFilteredList() with computed property
  filteredList = computed<FacetVOValue[]>(() => {
    const options = this.filterOptions() || [];
    return this.showMore() ? options : options.slice(0, 5);
  });

  displayText = computed<{ [key: string]: string }>(() => {
    const displayTextObject: { [key: string]: string } = {};
    if (this.attributeName() === GTGT_FILTER_NAME) {
      displayTextObject['Y'] = getGtgtDisplayValue(this.chain.chainIdentifierAbbr);
    }
    return displayTextObject;
  });

  ariaLabels = computed<string[]>(() => {
    return (
      this.filterOptions()?.map((filterOption) => {
        if (this.attributeName() === GTGT_FILTER_NAME) {
          return this.displayText()?.[filterOption.value];
        } else {
          return `${filterOption.value}, ${filterOption.count} products`;
        }
      }) ?? []
    );
  });

  displayValues = computed<string[]>(() => {
    return (
      this.filterOptions()?.map((filterOption) => {
        if (this.attributeName() === GTGT_FILTER_NAME) {
          return this.displayText()?.[filterOption.value];
        } else {
          return filterOption.value;
        }
      }) ?? []
    );
  });

  hasOverflow = computed<boolean>(() => (this.filterOptions()?.length ?? 0) > 5);

  toggleShowMore(): void {
    this.showMore.update((currentValue) => !currentValue);
    this.showMoreChange.emit(this.showMore());
  }

  setSelectedFilter(filterName: string): void {
    this.selectedFilter.emit(filterName);
  }

  setSelectedFilterWithSpace(event: Event, filterName: string): void {
    event.preventDefault();
    this.setSelectedFilter(filterName);
  }

  trackFilterName(filterOption: { value: string }): string {
    return this.attributeIdentifier() + filterOption.value;
  }
}
